<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-robot-outline me-2"></i>{{ $t('page.ai', 'AI Clinical Assistant') }}</h4>
        </div>
        <button v-if="messages.length" class="hms-btn hms-btn-blue" @click="reset">
          <i class="mdi mdi-restart me-1"></i>New Conversation
        </button>
      </div>

      <!-- Mode tabs -->
      <div class="hms-mode-tabs mb-3">
        <button v-for="m in modes" :key="m.value"
                class="hms-mode-tab"
                :class="{ active: mode === m.value }"
                :disabled="messages.length > 0 && mode !== m.value"
                :style="mode === m.value ? 'border-color:' + m.color + ';background:' + m.color + '20;color:' + m.color : ''"
                @click="setMode(m.value)">
          <i :class="m.icon" class="me-1"></i>{{ m.label }}
        </button>
      </div>

      <!-- Disclaimer -->
      <div class="hms-disclaimer mb-3">
        <i class="mdi mdi-shield-alert-outline me-2"></i>
        <span>For clinical reference only. AI suggestions must be reviewed by a qualified physician. Never use as a final diagnosis.</span>
      </div>

      <!-- Pre-conversation form -->
      <div v-if="!messages.length" class="hms-card">
        <div class="hms-section-label">PATIENT (optional context)</div>
        <div class="mb-3">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="patientSearch" placeholder="Link a patient — name, patient no, phone..." @input="onPatientSearch" />
          </div>
          <div v-if="patientResults.length" class="hms-suggest">
            <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
              <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
              <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.gender || '—' }} · {{ pp.phone || '—' }}</div>
            </div>
          </div>
          <div v-if="patientId" class="hms-picked mt-2">
            <i class="mdi mdi-check-circle me-1"></i>{{ patientName }}
            <span class="hms-muted-sm ms-2">{{ patientNo }} — context will be sent to the AI</span>
            <button class="hms-modal-close ms-auto" @click="clearPatient">&times;</button>
          </div>
          <div v-else class="hms-muted-sm mt-1">Skip if you're asking a general question.</div>
        </div>

        <div class="hms-section-label">YOUR QUESTION</div>
        <div class="mb-3">
          <textarea class="hms-input" rows="6" v-model="initialPrompt" :placeholder="placeholder"></textarea>
        </div>

        <div class="hms-section-label">IMAGE (optional — for vision analysis)</div>
        <div class="mb-3">
          <div class="hms-photo-row">
            <div v-if="photo" class="hms-photo-thumb">
              <img :src="photo.preview" />
              <button class="hms-photo-remove" @click="photo = null"><i class="mdi mdi-close"></i></button>
            </div>
            <label v-else-if="!compressing" class="hms-photo-add">
              <input type="file" accept="image/*" @change="onPhotoPick" />
              <i class="mdi mdi-camera-plus"></i>
              <span>Add image</span>
            </label>
            <div v-else class="hms-photo-add"><span class="spinner-border spinner-border-sm"></span></div>
          </div>
          <div class="hms-muted-sm mt-1">e.g. lab report, X-ray, ECG, skin condition photo. Auto-compressed.</div>
        </div>

        <div v-if="error" class="hms-alert hms-alert-danger">{{ error }}</div>

        <button class="hms-btn hms-btn-primary w-100 hms-btn-lg" @click="submit" :disabled="loading || (!initialPrompt.trim() && !photo)">
          <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="mdi mdi-send me-1"></i>
          {{ loading ? 'Asking Claude...' : 'Ask AI Assistant' }}
        </button>
      </div>

      <!-- Conversation -->
      <div v-else class="hms-conv-wrap">
        <div class="hms-card hms-conv">
          <div v-for="(m, mi) in messages" :key="mi" class="hms-msg" :class="'hms-msg-' + m.role">
            <div class="hms-msg-avatar">
              <i v-if="m.role === 'assistant'" class="mdi mdi-robot-outline"></i>
              <i v-else class="mdi mdi-account"></i>
            </div>
            <div class="hms-msg-bubble">
              <img v-if="m.photo" :src="m.photo" class="hms-msg-image" />
              <div class="hms-msg-content" v-html="formatMessage(m.content)"></div>
            </div>
          </div>
          <div v-if="loading" class="hms-msg hms-msg-assistant">
            <div class="hms-msg-avatar"><i class="mdi mdi-robot-outline"></i></div>
            <div class="hms-msg-bubble">
              <span class="spinner-border spinner-border-sm me-2"></span>
              <span class="hms-muted">Claude is thinking...</span>
            </div>
          </div>
        </div>

        <div v-if="error" class="hms-alert hms-alert-danger mt-2">{{ error }}</div>

        <div class="hms-followup mt-3">
          <div class="d-flex gap-2 align-items-end">
            <textarea class="hms-input" rows="2" v-model="followupText" placeholder="Follow-up question..." @keydown.enter.exact.prevent="sendFollowup"></textarea>
            <button class="hms-btn hms-btn-primary" @click="sendFollowup" :disabled="loading || !followupText.trim()">
              <i class="mdi mdi-send"></i>
            </button>
          </div>
          <div class="hms-muted-sm mt-2 text-center">
            <i class="mdi mdi-shield-alert-outline me-1"></i>AI advice is informational only — physician review required.
          </div>
        </div>
      </div>

      <!-- Quick tools row (only when no conversation) -->
      <div v-if="!messages.length" class="hms-tools mt-3">
        <div class="hms-tools-label">QUICK TOOLS</div>
        <div class="hms-tools-grid">
          <div v-for="t in quickTools" :key="t.label" class="hms-tool" @click="loadPrompt(t)" :title="t.help">
            <i :class="t.icon" :style="'color:' + t.color"></i>
            <span>{{ t.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 80px; max-width: 920px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }

.hms-mode-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-mode-tab {
  background: var(--hms-bg1);
  border: 1px solid var(--hms-br);
  color: var(--hms-m);
  border-radius: 50px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  display: inline-flex;
  align-items: center;
}
.hms-mode-tab:not(:disabled):hover { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-mode-tab:disabled { opacity: 0.45; cursor: not-allowed; }
.hms-mode-tab.active { font-weight: 700; }

.hms-disclaimer {
  background: rgba(245,130,31,.08);
  border: 1px solid rgba(245,130,31,.3);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12px;
  color: var(--hms-h);
  display: flex;
  align-items: center;
}
.hms-disclaimer i { color: var(--hms-accent); font-size: 18px; }

.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }

.hms-search-wrap { position: relative; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 10px 14px; color: var(--hms-h); font-size: 14px; outline: none; line-height: 1.5; }
.hms-input:focus { border-color: var(--hms-primary); }

.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 20px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-photo-row { display: flex; gap: 8px; }
.hms-photo-thumb { position: relative; width: 130px; height: 130px; border-radius: 10px; overflow: hidden; background: var(--hms-bg2); border: 1px solid var(--hms-br); }
.hms-photo-thumb img { width: 100%; height: 100%; object-fit: cover; }
.hms-photo-remove { position: absolute; top: 4px; right: 4px; width: 26px; height: 26px; border-radius: 50%; background: rgba(0,0,0,.7); border: none; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.hms-photo-add { width: 130px; height: 130px; border-radius: 10px; border: 2px dashed var(--hms-br); background: var(--hms-bg2); display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; color: var(--hms-s); transition: all .15s; }
.hms-photo-add:hover { border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-photo-add i { font-size: 28px; }
.hms-photo-add span { font-size: 11px; margin-top: 6px; }
.hms-photo-add input { display: none; }

.hms-btn { border: none; border-radius: 8px; padding: 10px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-lg { padding: 12px 20px; font-size: 14px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

/* Conversation */
.hms-conv { padding: 14px; max-height: 60vh; overflow-y: auto; }
.hms-msg { display: flex; gap: 10px; margin-bottom: 14px; }
.hms-msg-user { flex-direction: row-reverse; }
.hms-msg-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: var(--hms-bg3);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  color: var(--hms-primary);
}
.hms-msg-user .hms-msg-avatar { background: var(--hms-primary-lt); }
.hms-msg-bubble {
  background: var(--hms-bg2);
  border: 1px solid var(--hms-br);
  border-radius: 12px;
  padding: 12px 16px;
  max-width: 80%;
  font-size: 14px;
  line-height: 1.6;
  color: var(--hms-h);
}
.hms-msg-user .hms-msg-bubble { background: var(--hms-primary-lt); border-color: rgba(26,127,232,.3); }
.hms-msg-image { max-width: 240px; max-height: 240px; border-radius: 8px; display: block; margin-bottom: 8px; }
.hms-msg-content { white-space: pre-wrap; word-break: break-word; }
.hms-msg-content :deep(strong) { color: var(--hms-primary); font-weight: 700; }
.hms-msg-content :deep(em) { color: var(--hms-m); font-style: italic; }
.hms-msg-content :deep(code) { background: var(--hms-primary-lt); color: var(--hms-primary); padding: 1px 4px; border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-msg-content :deep(.hms-h1), .hms-msg-content :deep(.hms-h2), .hms-msg-content :deep(.hms-h3) { color: var(--hms-primary); font-weight: 700; margin: 10px 0 4px; }
.hms-msg-content :deep(.hms-h2) { text-transform: uppercase; letter-spacing: 0.5px; font-size: 13px; }
.hms-msg-content :deep(.hms-h3) { font-size: 13px; }
.hms-msg-content :deep(.hms-bul) { color: var(--hms-primary); font-weight: 700; }

.hms-followup { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px; }

/* Quick tools */
.hms-tools { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 16px 20px; }
.hms-tools-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 10px; }
.hms-tools-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 8px; }
.hms-tool {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--hms-br);
  border-radius: 10px;
  background: var(--hms-bg2);
  cursor: pointer;
  transition: all .15s;
  font-size: 13px;
  color: var(--hms-h);
}
.hms-tool:hover { border-color: var(--hms-primary); transform: translateY(-1px); }
.hms-tool i { font-size: 20px; }

@media (max-width: 768px) {
  .hms-msg-bubble { max-width: 90%; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

function compressImage(file, maxDim, quality) {
  return new Promise(function(resolve) {
    var reader = new FileReader();
    reader.onload = function(e) {
      var img = new Image();
      img.onload = function() {
        var w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
          else       { w = Math.round(w * maxDim / h); h = maxDim; }
        }
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        var dataUrl = canvas.toDataURL('image/jpeg', quality);
        var b64 = dataUrl.split(',')[1] || '';
        resolve({ preview: dataUrl, base64: b64, mediaType: 'image/jpeg' });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default {
  name: "AIAssistant",
  components: { Navigation },
  data() {
    return {
      mode: "general",
      modes: [
        { value: "general",    label: "General Q&A",      icon: "mdi mdi-chat-question", color: "#1a7fe8" },
        { value: "diagnosis",  label: "Differential Dx",  icon: "mdi mdi-stethoscope",   color: "#e74c3c" },
        { value: "note",       label: "Draft Note",       icon: "mdi mdi-notebook-edit", color: "#9b59b6" },
        { value: "lab",        label: "Lab Interpretation",icon: "mdi mdi-flask",        color: "#50a5f1" },
        { value: "icd",        label: "ICD-10 Codes",     icon: "mdi mdi-tag-text",      color: "#f59e0b" },
        { value: "medication", label: "Medication Check", icon: "mdi mdi-pill",          color: "#34c38f" }
      ],

      patientId: "",
      patientName: "",
      patientNo: "",
      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],

      initialPrompt: "",
      followupText: "",
      photo: null,
      compressing: false,

      messages: [],
      loading: false,
      error: "",

      quickTools: [
        { label: "Headache differential",   icon: "mdi mdi-head-question-outline", color: "#e74c3c", mode: "diagnosis", prompt: "30 year old female, severe throbbing headache for 6 hours, photophobia, nausea, no neck stiffness, no fever. What are the differentials?" },
        { label: "Format SOAP note",        icon: "mdi mdi-notebook-edit", color: "#9b59b6", mode: "note",      prompt: "Patient came in with cough, fever 38.4, productive sputum 3 days. On exam crackles right lower zone, RR 24, SpO2 94%. Started amoxicillin 500mg TDS, paracetamol PRN. Follow up in 5 days. Format as SOAP." },
        { label: "Suggest ICD codes",       icon: "mdi mdi-tag-text",      color: "#f59e0b", mode: "icd",       prompt: "Acute exacerbation of chronic obstructive pulmonary disease with respiratory failure" },
        { label: "Interpret HbA1c",         icon: "mdi mdi-chart-line",    color: "#50a5f1", mode: "lab",       prompt: "T2DM patient, HbA1c 10.2%, FBS 14.8, on metformin 1g BD + Mixtard 28u BD. What's the interpretation and next steps?" },
        { label: "Drug dosing in CKD",      icon: "mdi mdi-pill",          color: "#34c38f", mode: "medication",prompt: "65 year old, CKD stage 4 (eGFR ~22), needs amoxicillin for community pneumonia. What dose?" },
        { label: "Pre-eclampsia management",icon: "mdi mdi-mother-heart",  color: "#e84393", mode: "diagnosis", prompt: "G2P1 at 36 weeks, BP 165/110, +1 proteinuria, mild headache, no visual disturbances. Management approach?" }
      ]
    };
  },
  computed: {
    placeholder() {
      var map = {
        general:    "Ask any clinical question — e.g. 'Best first-line for community-acquired pneumonia in a Kenyan adult?'",
        diagnosis:  "Describe the presentation — age, sex, symptoms, duration, key findings — for differential diagnosis...",
        note:       "Paste raw consultation notes; specify SOAP / discharge / referral. The AI will format them.",
        lab:        "Describe the patient and paste lab values to get an interpretation.",
        icd:        "Type the diagnosis text. The AI will return ICD-10 candidates with descriptions.",
        medication: "Ask about a drug, dose, or interaction in context of the patient."
      };
      return map[this.mode] || "";
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    setMode(m) {
      if (this.messages.length && this.mode !== m) return;
      this.mode = m;
    },
    loadPrompt(t) {
      this.mode = t.mode;
      this.initialPrompt = t.prompt;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch(e) {}
      }, 300);
    },
    pickPatient(p) {
      this.patientId   = p.patient_id;
      this.patientName = (p.first_name + ' ' + p.last_name).trim();
      this.patientNo   = p.patient_no;
      this.patientResults = []; this.patientSearch = "";
    },
    clearPatient() {
      this.patientId = ""; this.patientName = ""; this.patientNo = "";
    },
    async onPhotoPick(e) {
      var f = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!f || f.type.indexOf("image/") !== 0) return;
      this.compressing = true;
      try {
        this.photo = await compressImage(f, 1200, 0.8);
      } catch(err) { this.error = "Failed to process image"; }
      this.compressing = false;
    },
    formatMessage(text) {
      if (!text) return "";
      var s = text.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
      // Headings (## first so bold doesn't grab)
      s = s.replace(/^###\s+(.+)$/gm, '<div class="hms-h3">$1</div>');
      s = s.replace(/^##\s+(.+)$/gm,  '<div class="hms-h2">$1</div>');
      s = s.replace(/^#\s+(.+)$/gm,   '<div class="hms-h1">$1</div>');
      // Bold + italic
      s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
      // Lists
      s = s.replace(/^\s*(\d+)\.\s+/gm, '<span class="hms-bul">$1.</span> ');
      s = s.replace(/^[-•]\s+/gm,        '<span class="hms-bul">•</span> ');
      // Inline code
      s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
      // Paragraphs
      s = s.replace(/\n\n+/g, "<br><br>");
      s = s.replace(/\n/g, "<br>");
      return s;
    },

    async submit() {
      if (this.loading) return;
      var question = this.initialPrompt.trim();
      if (!question && !this.photo) return;
      var msg = { role: "user", content: question || "[image attached]" };
      if (this.photo) msg.photo = this.photo.preview;
      this.messages.push(msg);
      await this.callAi(question, this.photo);
      this.initialPrompt = "";
    },
    async sendFollowup() {
      if (this.loading || !this.followupText.trim()) return;
      var q = this.followupText.trim();
      this.messages.push({ role: "user", content: q });
      this.followupText = "";
      await this.callAi(q, null);
    },

    async callAi(question, photoData) {
      this.loading = true;
      this.error = "";
      try {
        // Build chat history (everything BEFORE the latest user message we just pushed)
        var history = this.messages.slice(0, -1).map(function(m) {
          return { role: m.role, content: m.content };
        });
        var params = {
          function:     "aiClinicalAssist",
          mode:         this.mode,
          patient_id:   this.patientId || "",
          question:     question,
          chat_history: JSON.stringify(history)
        };
        if (photoData) {
          params.image_base64     = photoData.base64;
          params.image_media_type = photoData.mediaType;
        }
        var r = await executeJson(params, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.response) {
          this.messages.push({ role: "assistant", content: r.data.data.response });
          // Clear photo after first send
          this.photo = null;
        } else {
          this.error = (r.data && r.data.message) || "Could not get AI response.";
        }
      } catch(e) {
        this.error = "Connection error. Please try again.";
      }
      this.loading = false;
    },

    reset() {
      this.messages = [];
      this.followupText = "";
      this.error = "";
      this.photo = null;
    }
  }
};
</script>
